import { useMemo } from 'react';
import { Tag, Typography, Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  SmartDataTableProps,
  SmartTableColumn,
} from '@src/components/SmartDataTable/types';
import { useEngagementModeRender } from './useEngagementModeRender';

import { useStatusRender } from './useStatusRender';
import { useSourceRender } from './useSourceRender';
import {
  useUserDateRender,
  useUserTimestampRender,
  useScheduledDateRender,
  useUserOptionRender,
} from '@src/hooks';
import {
  SOURCE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  ENGAGEMENT_MODE_FILTER_OPTIONS,
  INTEREST_FILTER_OPTIONS,
} from '../constants';
import { FaWhatsapp } from 'react-icons/fa';

const { Text } = Typography;

interface UseLeadsColumnsOptions {
  showCreatedAt?: boolean;
}

export const useLeadsColumns = (
  options: UseLeadsColumnsOptions = {}
): SmartTableColumn[] => {
  const { showCreatedAt = true } = options;
  const userOptionRender = useUserOptionRender();
  const getStatusConfig = useStatusRender();
  const getEngagementModeConfig = useEngagementModeRender();
  const getSourceConfig = useSourceRender();
  const renderFollowUpDate = useScheduledDateRender();
  const renderCreatedDate = useUserDateRender('created');
  const renderUpdatedDate = useUserDateRender('updated');
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
    showOnlyCreated: true,
  });

  // Handle patient info click to redirect to patient details
  const handlePatientInfoClick = (record: any, e: any) => {
    e.stopPropagation();
    // Only redirect if patient_id exists and is not 0
    if (record.patient_id && record.patient_id !== 0) {
      const url = `${window.location.origin}/patients/${record.patient_id}`;
      window.open(url, '_blank');
    }
  };

  return useMemo(() => {
    const baseColumns: SmartTableColumn[] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'patient',
        title: 'Patient Info',
        dataIndex: 'patient_name',
        sorter: false,
        minWidth: 200,
        render: (patientName: any, record: any) => {
          const isClickable = record.patient_id && record.patient_id !== 0;

          return (
            <div
              onClick={e => handlePatientInfoClick(record, e)}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                padding: '2px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
              }}
              onMouseEnter={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                }
              }}
              onMouseLeave={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
              title={isClickable ? 'Click to view patient details' : undefined}
            >
              <div style={{ fontWeight: 500, color: '#262626' }}>
                {patientName || 'N/A'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#8c8c8c',
                  }}
                >
                  +{record.patient_mobile_no || 'No mobile'}
                </div>
                {record.patient_mobile_no && (
                  <Tooltip title="Open WhatsApp">
                    <FaWhatsapp
                      style={{ fontSize: '18px', color: '#25D366' }}
                      onClick={e => {
                        e.stopPropagation();
                        if (record.patient_mobile_no) {
                          window.open(
                            `https://web.whatsapp.com/send/?phone=${record.patient_mobile_no}`,
                            '_blank'
                          );
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        minWidth: 130,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: (status: any) => getStatusConfig(status),
      },
      {
        key: 'source',
        title: 'Source',
        dataIndex: 'source',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...SOURCE_FILTER_OPTIONS],
        render: (source: any) => getSourceConfig(source),
      },
      {
        key: 'interest',
        title: 'Interest',
        dataIndex: 'interest',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect' as const,
        filterOptions: [...INTEREST_FILTER_OPTIONS],
        render: (value: string) => {
          if (!value) return <Text type="secondary"></Text>;

          // Split comma-separated string into array and render as multiple tags
          const interests = value.split(',').filter(Boolean);

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {interests.map((interest, index) => (
                <Tag
                  key={index}
                  color="blue"
                  style={{ margin: 0, fontSize: '11px' }}
                >
                  {interest.trim()}
                </Tag>
              ))}
            </div>
          );
        },
      },
      // {
      //   key: 'follow_up_date',
      //   title: 'Follow Up',
      //   dataIndex: 'follow_up_date',
      //   sorter: true,
      //   minWidth: 140,
      //   filterable: true,
      //   filterType: 'daterange' as const,
      //   render: (followUpDate: any, record: any) =>
      //     renderFollowUpDate(followUpDate, record.status),
      // },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [
    userOptionRender,
    getStatusConfig,
    getEngagementModeConfig,
    getSourceConfig,
    renderFollowUpDate,
    renderCreatedDate,
    renderUpdatedDate,
    userTimestampRender,
    showCreatedAt,
  ]);
};
